import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { toast } from '../../lib/useToast.js';
import { PageHeader, LoadingSkeleton, ErrorState } from '../../components/primitives.jsx';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { GlossaryTerm } from '../../components/primitives/GlossaryTerm.jsx';
import { WorkflowPipeline, buildPipelineSteps, StaggerReveal } from '../../components/index.js';
import { AccessRequestForm } from './AccessRequestForm.jsx';
import './access.redesign.css';

// The five-stage approval path every eSAAR request travels after submission.
const APPROVAL_ORDER = [
  'supervisor_review',
  'security_review',
  'portfolio_owner_review',
  'data_steward_review',
  'admin_provisioning',
];

const APPROVAL_LABELS = {
  supervisor_review: 'Supervisor',
  security_review: 'Security',
  portfolio_owner_review: 'Portfolio',
  data_steward_review: 'Steward',
  admin_provisioning: 'Provisioning',
};

const EXPECT_STEPS = [
  { key: 'supervisor_review', name: 'Supervisor review', desc: 'Your supervisor confirms the business need.' },
  { key: 'security_review', name: 'Security & compliance', desc: 'Security reviews the request against policy.' },
  { key: 'portfolio_owner_review', name: 'Portfolio owner', desc: 'The portfolio owner validates scope alignment.' },
  { key: 'data_steward_review', name: 'Data steward', desc: 'A steward verifies the requested data scopes.' },
  { key: 'admin_provisioning', name: 'Admin provisioning', desc: 'An admin provisions and activates your access.' },
];

// States the requester can edit + resubmit.
const EDITABLE_STATES = new Set(['draft', 'returned_for_rework']);

// missingItems keys -> plain-language attestation labels (never show raw keys).
const ATTESTATION_LABELS = {
  trainingComplete: {
    label: 'Required training',
    helper: 'Complete the mandatory system-use training.',
  },
  dataHandlingAcknowledgement: {
    label: 'Data handling acknowledgement',
    helper: "Acknowledge the data-handling and CUI rules for the data you'll touch.",
  },
  aiOutputReviewAcknowledgement: {
    label: 'AI-output review acknowledgement',
    helper: "Acknowledge you'll review AI-generated output before acting on it.",
  },
};

// The reviewer's most recent return note.
function returnNote(data) {
  const returned = (data?.workflowSteps || []).filter((s) => s.status === 'returned');
  return returned.length ? returned[returned.length - 1].note || null : null;
}

// Map an existing request's current state into the five-stage approval ribbon.
function existingApprovalSteps(data) {
  const current = data?.currentWorkflowState || data?.workflowSteps?.at(-1)?.workflowState || 'draft';
  const statusByKey = {};
  let anchor = current;
  if (current === 'denied' || data?.currentWorkflowState === 'returned_for_rework') {
    const reached = (data?.workflowSteps || [])
      .map((s) => s.workflowState)
      .filter((w) => APPROVAL_ORDER.includes(w));
    const blockKey = reached.at(-1) || APPROVAL_ORDER[0];
    statusByKey[blockKey] = 'blocked';
    anchor = blockKey;
  } else if (current === 'submitted' || current === 'draft') {
    anchor = null;
  } else if (current === 'provisioned' || current === 'active') {
    anchor = 'done-all';
  }
  if (anchor === 'done-all') {
    return APPROVAL_ORDER.map((key) => ({ key, label: APPROVAL_LABELS[key], status: 'done' }));
  }
  return buildPipelineSteps({ order: APPROVAL_ORDER, current: anchor, statusByKey, labels: APPROVAL_LABELS });
}

export function AccessRequestPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const existing = useApi(id ? `/access-requests/${id}` : null);
  const mut = useMutation();
  const [draft, setDraft] = useState({
    justification: '',
    requestedRoleIds: [],
    requestedScopes: [],
    riskLevel: 'standard',
  });
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);

  const data = existing.data?.data;

  // Seed the edit draft + reset attestations when a request loads / changes.
  useEffect(() => {
    if (!data) return;
    setDraft({
      justification: data.justification || '',
      requestedRoleIds: data.requestedRoleIds || [],
      requestedScopes: data.requestedScopes || [],
      requestedHomeOrganizationId: data.requestedHomeOrganizationId,
      requestedLandingRoute: data.requestedLandingRoute,
      riskLevel: data.riskLevel || 'standard',
    });
    setChecked({});
    setConflict(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  if (id && existing.loading) return <LoadingSkeleton rows={6} />;
  if (id && existing.error) return <ErrorState error={existing.error} />;

  const previewSteps = APPROVAL_ORDER.map((key) => ({ key, label: APPROVAL_LABELS[key], status: 'pending' }));
  const steps = data ? existingApprovalSteps(data) : previewSteps;
  const editable = Boolean(data) && EDITABLE_STATES.has(data.currentWorkflowState || 'draft');
  const missingItems = data?.missingItems || [];
  const allResolved = missingItems.length === 0 || missingItems.every((k) => checked[k]);
  const hasRoles = (draft.requestedRoleIds || []).length > 0;
  const note = returnNote(data);

  function buildPatchBody() {
    const attestations = {};
    for (const k of missingItems) attestations[k] = Boolean(checked[k]);
    const remaining = missingItems.filter((k) => !checked[k]);
    return {
      justification: draft.justification,
      requestedRoleIds: draft.requestedRoleIds,
      requestedScopes: draft.requestedScopes,
      requestedHomeOrganizationId: draft.requestedHomeOrganizationId,
      requestedLandingRoute: draft.requestedLandingRoute,
      riskLevel: draft.riskLevel,
      attestations,
      missingItems: remaining,
    };
  }

  async function submit() {
    const created = await mut.run(() => api.post('/access-requests', draft));
    const newId = created.data?.id || created.id;
    if (newId) {
      await api.post(`/access-requests/${newId}/actions`, { action: 'submit' });
      navigate('/access/status');
    }
  }

  async function saveEdits() {
    setBusy(true);
    try {
      await api.patch(`/access-requests/${id}`, buildPatchBody());
      toast.success('Saved — your changes are stored.');
      existing.refresh?.();
    } finally {
      setBusy(false);
    }
  }

  async function resubmit() {
    setBusy(true);
    setConflict(false);
    try {
      // Optimistic guard: re-read state first; if a reviewer already advanced it,
      // don't resubmit into a stale state.
      const fresh = await api.get(`/access-requests/${id}`);
      const freshState = fresh?.data?.currentWorkflowState || 'draft';
      if (!EDITABLE_STATES.has(freshState)) {
        setConflict(true);
        existing.refresh?.();
        return;
      }
      await api.patch(`/access-requests/${id}`, buildPatchBody());
      await api.post(`/access-requests/${id}/actions`, { action: 'submit' });
      toast.success('Resubmitted — your request is back in the review queue.');
      navigate('/access/status');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={<GlossaryTerm term="eSAAR" />}
        title={data ? `Request ${data.id}` : 'Request access'}
        subtitle="Submit an eSAAR request — supervisor → security → portfolio → steward → provisioning."
      />
      <div className="access-page">
        <section className="card access-pipeline-block">
          <h2 className="card-title">Approval path</h2>
          <p className="access-pipeline-block__caption">
            Five reviewers sign off in sequence before access is provisioned.
          </p>
          <WorkflowPipeline steps={steps} ariaLabel="eSAAR five-stage approval path" />
        </section>

        {data && editable ? (
          <section className="card">
            <h2 className="card-title">Fix &amp; resubmit</h2>
            {note ? (
              <div className="access-return-note" role="note">
                <span className="access-return-note__label">Returned for rework</span>
                <span className="access-return-note__text">{note}</span>
              </div>
            ) : null}

            {missingItems.length ? (
              <div className="access-attest">
                <h3 className="access-attest__title">What to fix before resubmitting</h3>
                <ul className="access-attest-list">
                  {missingItems.map((k) => {
                    const meta = ATTESTATION_LABELS[k] || { label: k, helper: '' };
                    const isChecked = Boolean(checked[k]);
                    return (
                      <li key={k} className={`access-attest-item${isChecked ? ' is-checked' : ''}`}>
                        <label className="access-attest-item__label">
                          <input
                            type="checkbox"
                            aria-label={meta.label}
                            checked={isChecked}
                            onChange={() => setChecked((c) => ({ ...c, [k]: !c[k] }))}
                          />
                          <span className="access-attest-item__text">
                            <span className="access-attest-item__name">{meta.label}</span>
                            {meta.helper ? (
                              <span className="access-attest-item__helper">{meta.helper}</span>
                            ) : null}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {allResolved ? (
                  <p className="access-attest__done" role="status">
                    All set — you can resubmit.
                  </p>
                ) : null}
              </div>
            ) : null}

            <AccessRequestForm value={draft} onChange={setDraft} />

            {conflict ? (
              <div className="access-return-note tone-brick" role="alert">
                <span className="access-return-note__label">Heads up</span>
                <span className="access-return-note__text">This request already moved forward.</span>
              </div>
            ) : null}

            <div className="access-form__actions">
              <button type="button" className="btn" onClick={saveEdits} disabled={busy}>
                Save changes
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={resubmit}
                disabled={busy || !allResolved}
              >
                {busy ? 'Resubmitting…' : 'Resubmit'}
              </button>
            </div>
          </section>
        ) : data ? (
          <section className="card">
            <h2 className="card-title">Workflow</h2>
            <WorkflowPipeline steps={steps} ariaLabel={`eSAAR approval chain for ${data.id}`} />
            {(data.workflowSteps || []).length ? (
              <ol className="access-expect__list" style={{ marginTop: 'var(--space-4)' }}>
                {data.workflowSteps.map((s, i) => {
                  const tone =
                    s.status === 'denied' || s.status === 'returned'
                      ? 'brick'
                      : s.status === 'complete'
                        ? 'sage'
                        : 'slate';
                  return (
                    <li key={i} className="access-expect__step">
                      <span className="access-chip__dot" aria-hidden style={{ marginTop: 6 }} />
                      <span>
                        <span className="access-expect__step-name">{s.stepName}</span>
                        <span className="access-chip-row" style={{ marginTop: 4 }}>
                          <span className={`access-chip tone-${tone}`}>
                            <span className="access-chip__dot" />
                            {s.status || 'pending'}
                          </span>
                          {s.actorUserId ? (
                            <span className="access-expect__step-desc">
                              <PersonLabel userId={s.actorUserId} />
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </section>
        ) : (
          <StaggerReveal as="section" className="access-request-grid">
            <div className="card">
              <h2 className="card-title">New request</h2>
              <AccessRequestForm value={draft} onChange={setDraft} />
              <div className="access-form__actions">
                <button
                  className="btn primary"
                  onClick={submit}
                  disabled={mut.running || !draft.justification.trim() || !hasRoles}
                >
                  {mut.running ? 'Submitting…' : 'Submit'}
                </button>
                {!hasRoles ? <span className="access-form__hint">Pick at least one role.</span> : null}
              </div>
            </div>

            <aside className="card access-expect">
              <h3 className="access-expect__title">What to expect</h3>
              <ol className="access-expect__list">
                {EXPECT_STEPS.map((s, i) => (
                  <li key={s.key} className="access-expect__step">
                    <span className="access-expect__num" aria-hidden>
                      {i + 1}
                    </span>
                    <span>
                      <span className="access-expect__step-name">{s.name}</span>
                      <span className="access-expect__step-desc">{s.desc}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </aside>
          </StaggerReveal>
        )}
      </div>
    </>
  );
}
